// ===========================================
// GUEST GUARD - Public Only Routes
// ===========================================

import { type ReactNode } from 'react';
import { Navigate, useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsAuthenticated } from '@/features/auth/store/authSlice';
import { appConfig } from '@/config';

interface GuestGuardProps {
  children: ReactNode;
}

/**
 * Guard component for guest-only routes (sign-in, sign-up, etc.)
 * Redirects to account dashboard if user is already authenticated
 */
export function GuestGuard({ children }: GuestGuardProps) {
  const location = useLocation();
  const { lang } = useParams<{ lang: string }>();
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const currentLang = lang || appConfig.defaultLanguage;

  // Redirect to account if already authenticated
  if (isAuthenticated) {
    // Check for redirect URL in query params
    const searchParams = new URLSearchParams(location.search);
    const redirectUrl = searchParams.get('redirect');

    if (redirectUrl) {
      return <Navigate to={decodeURIComponent(redirectUrl)} replace />;
    }

    // Default redirect to account dashboard
    return <Navigate to={`/${currentLang}/account`} replace />;
  }

  return <>{children}</>;
}

export default GuestGuard;
